import { createSlice } from '@reduxjs/toolkit';
import { SearchStatus } from '../../constants';
import { logout } from './user';

// Keyed by tripSegmentId, which runs 1..N inside every trip, so the checkout belongs to one trip at a time.
const emptyTourCheckout = {
  tourTravelers: {},
  tourTravelerQuestions: {},
  tourOperatorNotes: {},
  tourHotelPickup: {},
  invalidTourTravelersBySegmentId: {},
  invalidTourQuestionsBySegmentId: {},
  invalidTourPickupBySegmentId: {},
};

const initialState = {
  tourSearches: {},
  details: {},
  cruiseLines: [],
  cruiseLinesStatus: SearchStatus.INITIAL,
  providerAccount: { token: null, doBookAsAgency: false, error: null },
  hasUserInteractedWithTourList: false,
  checkoutTripId: null,
  ...emptyTourCheckout,
};

// Results are kept per trip, the way hotelSearches keys its own; the search parameters (destination,
// dates, filters, sort, page) are global in tripSearch.searchObj.tour and shared by every panel and the
// main page. A search made from the main page lands in tripSearch.searchResult, never in this map.
const tourSearchesWith = (state, tripId, changes) => {
  if (!tripId) {
    return state.tourSearches;
  }

  return {
    ...state.tourSearches,
    [tripId]: {
      ...state.tourSearches[tripId],
      ...changes,
    },
  };
};

const tourSlice = createSlice({
  name: 'tour',
  initialState,
  reducers: {
    clearTourResults: (state, { payload }) => ({
      ...state,
      tourSearches: tourSearchesWith(state, payload && payload.tripId, {
        responses: {},
        error: null,
      }),
    }),
    tourSearchSuccess: (state, { payload: { tripId, response } }) => ({
      ...state,
      tourSearches: tourSearchesWith(state, tripId, {
        status: SearchStatus.DONE,
        error: null,
        responses: {
          ...response,
        },
      }),
    }),
    tourSearchFail: (state, { payload: { tripId, error } }) => ({
      ...state,
      tourSearches: tourSearchesWith(state, tripId, {
        status: SearchStatus.ERROR,
        error,
        responses: {},
      }),
    }),
    updateTourSearchObj: (state, { payload: { tripId, searchStatus } }) => ({
      ...state,
      tourSearches: tourSearchesWith(state, tripId, { status: searchStatus }),
    }),
    tourDetailsRequest: (state, { payload: { productId } }) => ({
      ...state,
      hasUserInteractedWithTourList: true,
      details: {
        ...state.details,
        [productId]: {
          ...state.details[productId],
          status: SearchStatus.SEARCHING,
          error: null,
        },
      },
    }),
    tourDetailsSuccess: (state, { payload: { productId, response } }) => ({
      ...state,
      details: {
        ...state.details,
        [productId]: {
          ...state.details[productId],
          status: SearchStatus.DONE,
          error: null,
          product: response && response.product,
          availabilityMessage: response && response.availabilityMessage,
        },
      },
    }),
    tourAvailabilityRequest: (state, { payload: { productId, isCalendar } }) => ({
      ...state,
      hasUserInteractedWithTourList: true,
      details: {
        ...state.details,
        [productId]: {
          ...state.details[productId],
          ...(isCalendar
            ? { calendarStatus: SearchStatus.SEARCHING, calendarError: null }
            : { quoteStatus: SearchStatus.SEARCHING, quoteError: null }),
        },
      },
    }),
    tourAvailabilitySuccess: (
      state,
      { payload: { productId, response, isCalendar, month, doReplaceDays, quoteKey } },
    ) => {
      const details = state.details[productId] || {};
      if (!isCalendar) {
        return {
          ...state,
          details: {
            ...state.details,
            [productId]: {
              ...details,
              quoteStatus: SearchStatus.DONE,
              quoteError: null,
              quote: response,
              quoteKey,
            },
          },
        };
      }

      const known = doReplaceDays ? [] : (details.availability && details.availability.days) || [];
      const knownDates = new Set(known.map((day) => day.date));
      const days = [
        ...known,
        ...((response && response.days) || []).filter((day) => !knownDates.has(day.date)),
      ];
      days.sort((left, right) => (left.date < right.date ? -1 : 1));

      const header = doReplaceDays || !details.availability ? response : details.availability;

      return {
        ...state,
        details: {
          ...state.details,
          [productId]: {
            ...details,
            calendarStatus: SearchStatus.DONE,
            calendarError: null,
            availability: {
              ...header,
              days,
              months: doReplaceDays
                ? [month]
                : [
                    ...new Set([
                      ...((details.availability && details.availability.months) || []),
                      month,
                    ]),
                  ],
            },

            availabilityMessage: response ? response.message : details.availabilityMessage,
          },
        },
      };
    },
    tourAvailabilityFail: (state, { payload: { productId, error, isCalendar } }) => ({
      ...state,
      details: {
        ...state.details,
        [productId]: {
          ...state.details[productId],
          ...(isCalendar
            ? { calendarStatus: SearchStatus.DONE, calendarError: error }
            : { quoteStatus: SearchStatus.DONE, quoteError: error }),
        },
      },
    }),
    tourCruiseLinesRequest: (state) => ({
      ...state,
      cruiseLinesStatus: SearchStatus.SEARCHING,
    }),
    tourCruiseLinesSuccess: (state, { payload: { response } }) => ({
      ...state,
      cruiseLinesStatus: SearchStatus.DONE,
      cruiseLines: response || [],
    }),
    tourCruiseLinesFail: (state) => ({
      ...state,
      cruiseLinesStatus: SearchStatus.ERROR,
      cruiseLines: [],
    }),
    changeTourProviderAccount: (state, { payload }) => ({
      ...state,
      providerAccount: {
        ...state.providerAccount,
        ...payload,
      },
    }),
    bindTourCheckout: (state, { payload: { tripId } }) => {
      if (state.checkoutTripId === tripId) {
        return state;
      }

      return { ...state, ...emptyTourCheckout, checkoutTripId: tripId };
    },
    changeTourSegmentField: (state, { payload: { field, segmentId, value } }) => ({
      ...state,
      [field]: { ...state[field], [segmentId]: value },
    }),
    setTourValidation: (
      state,
      {
        payload: {
          invalidTourTravelersBySegmentId = {},
          invalidTourQuestionsBySegmentId = {},
          invalidTourPickupBySegmentId = {},
        },
      },
    ) => ({
      ...state,
      invalidTourTravelersBySegmentId,
      invalidTourQuestionsBySegmentId,
      invalidTourPickupBySegmentId,
    }),
    tourRemainingSearchRequest: (state) => ({
      ...state,
    }),
    tourRemainingSearchStop: (state) => ({
      ...state,
    }),
    setTourCatalogLoad: (state, { payload: { tripId, ...load } }) => ({
      ...state,
      tourSearches: tourSearchesWith(state, tripId, load),
    }),
    setIsUserInteractedWithTourList: (state, { payload }) => ({
      ...state,
      hasUserInteractedWithTourList: payload,
    }),
    tourDetailsFail: (state, { payload: { productId, error } }) => ({
      ...state,
      details: {
        ...state.details,
        [productId]: {
          ...state.details[productId],
          status: SearchStatus.DONE,
          error,
        },
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => ({
      ...state,
      ...emptyTourCheckout,
      checkoutTripId: null,
      providerAccount: initialState.providerAccount,
    }));
  },
});

export const {
  bindTourCheckout,
  clearTourResults,
  tourSearchSuccess,
  tourSearchFail,
  updateTourSearchObj,
  tourDetailsRequest,
  tourDetailsSuccess,
  tourDetailsFail,
  tourAvailabilityRequest,
  tourAvailabilitySuccess,
  tourAvailabilityFail,
  tourCruiseLinesRequest,
  tourCruiseLinesSuccess,
  tourCruiseLinesFail,
  changeTourProviderAccount,
  changeTourSegmentField,
  setTourValidation,
  tourRemainingSearchRequest,
  tourRemainingSearchStop,
  setTourCatalogLoad,
  setIsUserInteractedWithTourList,
} = tourSlice.actions;
export default tourSlice.reducer;
